import { useState, useEffect, useCallback } from 'react'
import { SimulationRun, SimulationMonth } from '@/lib/db/schema/simulations'
import { CreateManualSimulationInput, GenerateScenariosInput } from '@/lib/validation/simulation'

export function useSimulations(businessId: string | undefined) {
  const [simulations, setSimulations] = useState<SimulationRun[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSimulations = useCallback(async () => {
    if (!businessId) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/businesses/${businessId}/simulations`)
      const json = await res.json() as any
      if (res.ok && json.success) {
        setSimulations(json.data)
      } else {
        setError(json.error?.message || 'Failed to fetch simulations')
      }
    } catch (err: any) {
      setError(err.message || 'Network error')
    } finally {
      setIsLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    fetchSimulations()
  }, [fetchSimulations])

  const createManualSimulation = async (payload: CreateManualSimulationInput) => {
    if (!businessId) return { success: false, error: 'No business ID' }
    const res = await fetch(`/api/businesses/${businessId}/simulations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const json = await res.json() as any
    if (res.ok && json.success) {
      fetchSimulations()
      return { success: true, data: json.data }
    }
    return { success: false, error: json.error?.message || 'Failed to create simulation' }
  }

  const generateScenarios = async (payload: GenerateScenariosInput) => {
    if (!businessId) return { success: false, error: 'No business ID' }
    const res = await fetch(`/api/businesses/${businessId}/simulations/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const json = await res.json() as any
    if (res.ok && json.success) {
      fetchSimulations()
      return { success: true, data: json.data }
    }
    return { success: false, error: json.error?.message || 'Failed to generate scenarios' }
  }

  return { simulations, isLoading, error, refetch: fetchSimulations, createManualSimulation, generateScenarios }
}

export function useSimulationDetail(businessId: string | undefined, simId: string | undefined) {
  const [data, setData] = useState<{ run: SimulationRun, months: SimulationMonth[] } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDetail = useCallback(async () => {
    if (!businessId || !simId) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/businesses/${businessId}/simulations/${simId}`)
      const json = await res.json() as any
      if (res.ok && json.success) {
        setData(json.data)
      } else {
        setError(json.error?.message || 'Failed to fetch simulation detail')
      }
    } catch (err: any) {
      setError(err.message || 'Network error')
    } finally {
      setIsLoading(false)
    }
  }, [businessId, simId])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  return { data, isLoading, error, refetch: fetchDetail }
}
